import { type FC, useMemo, useState } from "react";

import {
  DrawerBackdrop,
  DrawerBody,
  DrawerCloseTrigger,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerRoot,
  DrawerTrigger,
} from "@ui/components/Drawer";
import { type FieldError } from "@ui/components/Form";
import { StrengthIndicator } from "@ui/components/StrengthIndicator";
import { useTranslations } from "@ui/providers/useTranslations";
import classnames from "classnames";

import { Button } from "@jti/core-components/components/Button";
import { type StockStatusElements } from "@jti/fe-common";
import type { MagentoStockStatus } from "@jti/magento-types";

import { FlavorList } from "./components/FlavorList";
import { FlavorSelectionResult } from "./components/FlavorSelectionResult";

import { type FlavorFamilyFilter, type SingleFlavorSelectorItem } from "./SingleFlavorSelector.types";

import styles from "./SingleFlavorSelector.module.scss";

export type SingleFlavorSelectorProps = {
  flavors: SingleFlavorSelectorItem[];
  initialSelection?: SingleFlavorSelectorItem;
  stockStatusElements: StockStatusElements;
  hasError?: boolean;
  onChange: (sku: string) => void;
  isProductDataLoading?: boolean;
  error?: FieldError;
  className?: string;
  drawerHeadline?: string;
  strengthDetails?: { name: string; rating: number };
};

const IN_STOCK: MagentoStockStatus = "IN_STOCK";
const AVAILABLE_IN_STORES: MagentoStockStatus = "AVAILABLE_IN_STORES";
const OUT_OF_STOCK: MagentoStockStatus = "OUT_OF_STOCK";

export const SingleFlavorSelector: FC<SingleFlavorSelectorProps> = ({
  flavors,
  onChange,
  stockStatusElements,
  initialSelection,
  isProductDataLoading = false,
  error,
  className,
  drawerHeadline,
  strengthDetails,
}) => {
  const { __ } = useTranslations();

  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [userSelection, setUserSelection] = useState<SingleFlavorSelectorItem | undefined>(initialSelection);
  const [userTemporarySelection, setUserTemporarySelection] = useState<SingleFlavorSelectorItem | undefined>(
    initialSelection
  );

  const flavorFamilyFilters: FlavorFamilyFilter[] = useMemo(() => {
    const allFlavorFamilyNames = flavors.flatMap(({ flavorFamily }) => flavorFamily ?? []);
    const flavorFamilyCountsMap = new Map<string, number>();

    for (const flavorFamilyName of allFlavorFamilyNames) {
      flavorFamilyCountsMap.set(flavorFamilyName, (flavorFamilyCountsMap.get(flavorFamilyName) ?? 0) + 1);
    }

    return Array.from(flavorFamilyCountsMap, ([name, count]) => ({ name, count }));
  }, [flavors]);

  const productsWithNonInStockStatus = useMemo(() => {
    const productInStockStatus = stockStatusElements?.[userSelection?.sku ?? ""];
    if (isProductDataLoading || !productInStockStatus) {
      return undefined;
    }

    if (!userSelection || productInStockStatus === IN_STOCK) {
      return undefined;
    }

    return productInStockStatus;
  }, [stockStatusElements, userSelection, isProductDataLoading]);

  const onCollectionChange = (selectedProduct: SingleFlavorSelectorItem) => {
    setUserSelection(selectedProduct);
    onChange(selectedProduct.sku);
    setIsSelectorOpen(false);
  };

  const onTemporaryCollectionChange = (selectedProduct: SingleFlavorSelectorItem) => {
    setUserTemporarySelection(selectedProduct);
  };

  const onDrawerClose = (event: React.MouseEvent) => {
    event.preventDefault();

    setUserSelection(userTemporarySelection);
    onChange(userTemporarySelection?.sku ?? "");
  };

  return (
    <>
      <section className={classnames(styles.wrapper, className)} data-testid="flavourSelector">
        <div
          className={classnames(styles.main, {
            [styles.active]: isSelectorOpen,
            [styles.hasError]: error || Boolean(productsWithNonInStockStatus),
          })}
        >
          <div className={styles.desktopView} data-testid="SingleFlavorSelector-desktopView">
            <DrawerRoot onCancelCallback={() => setIsSelectorOpen(false)}>
              <DrawerTrigger>
                <FlavorSelectionResult
                  selectedFlavor={userSelection}
                  flavorsCount={flavors.length}
                  isSelectorOpen={isSelectorOpen}
                  onClick={() => {
                    setUserTemporarySelection(userSelection);
                  }}
                />
              </DrawerTrigger>

              <DrawerContent aria-label={__("productdetails.sticks.drawerHeadline")}>
                <DrawerHeader>
                  <div className={styles.drawerHeader}>
                    <div>{drawerHeadline || __("productdetails.sticks.drawerHeadline")}</div>
                    {strengthDetails && (
                      <StrengthIndicator
                        name={__("productdetails.sticks.drawerHeadline.strength_name_decorator", [
                          strengthDetails.name,
                        ])}
                        strength={strengthDetails.rating}
                        isLarge
                      />
                    )}
                  </div>
                </DrawerHeader>

                <DrawerBody>
                  <FlavorList
                    items={flavors}
                    flavorFamilyFilters={flavorFamilyFilters}
                    stockStatusElements={stockStatusElements}
                    isProductDataLoading={isProductDataLoading}
                    isActiveCheck={(sku) => sku === userTemporarySelection?.sku}
                    onItemClick={onTemporaryCollectionChange}
                  />
                </DrawerBody>

                <DrawerFooter>
                  <DrawerCloseTrigger>
                    <Button
                      className={styles.submit}
                      onClick={onDrawerClose}
                      variant="primary"
                      data-testid="applySelectionButton"
                    >
                      {__("productdetails.sticks.applySelection")}
                    </Button>
                  </DrawerCloseTrigger>
                </DrawerFooter>
              </DrawerContent>
              <DrawerBackdrop />
            </DrawerRoot>
          </div>

          <div className={styles.mobileView} data-testid="SingleFlavorSelector-mobileView">
            <FlavorSelectionResult
              onClick={() => setIsSelectorOpen(!isSelectorOpen)}
              selectedFlavor={userSelection}
              flavorsCount={flavors.length}
              isSelectorOpen={isSelectorOpen}
            />

            <section className={styles.selector}>
              <FlavorList
                items={flavors}
                flavorFamilyFilters={flavorFamilyFilters}
                stockStatusElements={stockStatusElements}
                isProductDataLoading={isProductDataLoading}
                isActiveCheck={(sku) => sku === userSelection?.sku}
                onItemClick={onCollectionChange}
              />
            </section>
          </div>
        </div>
      </section>

      {productsWithNonInStockStatus === AVAILABLE_IN_STORES && (
        <div className={styles.error}>{__("single_flavor_selector.validation.error.available_in_store")}</div>
      )}

      {productsWithNonInStockStatus === OUT_OF_STOCK && (
        <div className={styles.error}>{__("single_flavor_selector.validation.error.out_of_stock")}</div>
      )}

      {!!error && <div className={styles.error}>{error.message}</div>}
    </>
  );
};
